/**
 * Export Service
 * Phase 2.2: Hard drive-friendly backup with ZIP + NDJSON format
 * Compliance-grade export with checksums and manifest
 */
import JSZip from 'jszip';
import { COLLECTIONS } from '../types/core';
import { useLicense } from '../hooks/useLicense';

// Export format version for compatibility
const EXPORT_VERSION = 1;
const APP_VERSION = '1.0.0';

/**
 * Export backup in ZIP format with manifest and NDJSON collections
 * @param {Object} options - Export configuration
 * @param {string} options.orgId - Organization identifier
 * @param {string[]} options.vesselIds - Vessel IDs to export
 * @param {Object} options.collections - Collections object for data access
 * @param {Object} options.org - Organization data
 * @returns {Promise<Blob>} ZIP file as blob
 */
export async function exportBackup({ orgId, vesselIds, collections, org }) {
  const zip = new JSZip();
  const exportedAt = new Date().toISOString();
  const counts = {};
  const checksums = {};

  // Export each vessel's collections
  for (const vesselId of vesselIds) {

    counts[vesselId] = {};
    
    // Create vessel folder
    const vesselFolder = zip.folder(`vessels/${vesselId}`);
    
    // Export each collection as NDJSON
    for (const [collectionKey, collectionName] of Object.entries(COLLECTIONS)) {
      try {
        const records = await collections.list(collectionName);
        const vesselRecords = records.filter(record => 
          record.vesselId === vesselId || 
          record.id?.startsWith(`${vesselId}:`) ||
          collectionName === 'meta' // Meta records are vessel-scoped differently
        );
        
        if (vesselRecords.length > 0) {
          const ndjsonContent = vesselRecords
            .map(record => JSON.stringify(record))
            .join('\n');
          
          const filename = `${collectionName}.ndjson`;
          vesselFolder.file(filename, ndjsonContent);
          
          // Calculate checksum
          const checksum = await calculateSHA256(ndjsonContent);
          checksums[`vessels/${vesselId}/${filename}`] = `sha256:${checksum}`;
          
          counts[vesselId][collectionName] = vesselRecords.length;

        }
      } catch (error) {

      }
    }
    
    // Export operations queue for forensics (optional)
    try {
      const opsRecords = await collections.list('opsQueue');
      const vesselOps = opsRecords.filter(op => op.vesselId === vesselId);
      
      if (vesselOps.length > 0) {
        const opsFolder = zip.folder('ops');
        const opsContent = vesselOps
          .map(op => JSON.stringify(op))
          .join('\n');
        
        opsFolder.file(`${vesselId}.ndjson`, opsContent);
        const checksum = await calculateSHA256(opsContent);
        checksums[`ops/${vesselId}.ndjson`] = `sha256:${checksum}`;
      }
    } catch (error) {

    }
  }

  // Get vessel metadata
  const vessels = [];
  for (const vesselId of vesselIds) {
    try {
      const vesselRecord = await collections.get('meta', `vessel_${vesselId}`);
      if (vesselRecord?.data) {
        vessels.push({
          id: vesselId,
          name: vesselRecord.data.name || vesselId
        });
      } else {
        vessels.push({ id: vesselId, name: vesselId });
      }
    } catch (error) {

      vessels.push({ id: vesselId, name: vesselId });
    }
  }

  // Get configuration hashes if available
  let configHash = '';
  let packsHash = '';
  try {
    const snapshotRecords = await collections.list('meta');
    const latestSnapshot = snapshotRecords
      .filter(record => record.id.startsWith('snapshot_'))
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
    
    if (latestSnapshot?.data) {
      configHash = latestSnapshot.data.configHash || '';
      packsHash = latestSnapshot.data.packsHash || '';
    }
  } catch (error) {

  }

  // Create manifest
  const manifest = {
    version: EXPORT_VERSION,
    appVersion: APP_VERSION,
    exportedAt,
    org: {
      id: orgId,
      name: org?.name || 'FleetSkipper Organization'
    },
    vessels,
    counts,
    hashes: {
      configHash,
      packsHash
    },
    checksums
  };

  // Add manifest to zip
  zip.file('manifest.json', JSON.stringify(manifest, null, 2));

  // Generate ZIP blob
  const zipBlob = await zip.generateAsync({ 
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 }
  });

  return zipBlob;
}

/**
 * Trigger download of backup file
 * @param {Blob} zipBlob - ZIP file blob
 * @param {string} orgName - Organization name for filename
 */
export function downloadBackup(zipBlob, orgName = 'FleetSkipper') {
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const filename = `FleetSkipper_Backup_${orgName.replace(/[^a-zA-Z0-9]/g, '_')}_${today}.zip`;
  
  const url = URL.createObjectURL(zipBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  
  // Cleanup
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Save backup to linked folder (File System Access API)
 * @param {Blob} zipBlob - ZIP file blob
 * @param {FileSystemDirectoryHandle} directoryHandle - Directory handle
 * @param {string} orgName - Organization name for filename
 */
export async function saveToLinkedFolder(zipBlob, directoryHandle, orgName = 'FleetSkipper') {
  if (!directoryHandle) {
    throw new Error('No linked folder configured');
  }

  const today = new Date().toISOString().split('T')[0];
  const filename = `FleetSkipper_Backup_${orgName.replace(/[^a-zA-Z0-9]/g, '_')}_${today}.zip`;
  
  try {
    const fileHandle = await directoryHandle.getFileHandle(filename, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(zipBlob);
    await writable.close();

    return filename;
  } catch (error) {

    throw error;
  }
}

/**
 * Clean up old backups based on retention policy
 * @param {FileSystemDirectoryHandle} directoryHandle - Directory handle
 * @param {Object} retentionPolicy - Retention configuration
 */
export async function cleanupOldBackups(directoryHandle, retentionPolicy = { daily: 30, monthly: 12 }) {
  if (!directoryHandle) return;

  try {
    const entries = [];
    for await (const [name, handle] of directoryHandle.entries()) {
      // 2026-06-30: Pass 2w rebrand (D-025) renamed write-path to FleetSkipper_Backup_*.
      // READ path accepts BOTH prefixes because existing users have SeaReady_Backup_*.zip
      // files on disk from before the rename — dropping the legacy prefix orphans them.
      // TODO: Pass 2w-followup-N+1 — drop SeaReady_Backup_ pattern after 2027-06-30
      // once legacy backups are aged out (12-month retention window).
      if ((name.startsWith('SeaReady_Backup_') || name.startsWith('FleetSkipper_Backup_')) && name.endsWith('.zip')) {
        const file = await handle.getFile();
        entries.push({ name, handle, lastModified: file.lastModified });
      }
    }

    // Sort by date (newest first)
    entries.sort((a, b) => b.lastModified - a.lastModified);

    // Keep daily backups (last N days)
    const dailyKeep = entries.slice(0, retentionPolicy.daily);
    const remaining = entries.slice(retentionPolicy.daily);

    // Keep monthly backups (first of each month for last N months)
    const monthlyKeep = [];
    const seenMonths = new Set();
    
    for (const entry of remaining) {
      const date = new Date(entry.lastModified);
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
      
      if (!seenMonths.has(monthKey) && monthlyKeep.length < retentionPolicy.monthly) {
        monthlyKeep.push(entry);
        seenMonths.add(monthKey);
      }
    }

    // Delete files not in keep lists
    const toKeep = new Set([...dailyKeep, ...monthlyKeep].map(e => e.name));
    const toDelete = entries.filter(e => !toKeep.has(e.name));

    for (const entry of toDelete) {
      try {
        await directoryHandle.removeEntry(entry.name);

      } catch (error) {

      }
    }

  } catch (error) {

  }
}

/**
 * Calculate SHA-256 hash of content
 * @param {string} content - Content to hash
 * @returns {Promise<string>} Hex hash
 */
async function calculateSHA256(content) {
  const encoder = new TextEncoder();
  const data = encoder.encode(content);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Get File System Access API support
 * @returns {boolean} True if supported
 */
export function supportsFileSystemAccess() {
  return 'showDirectoryPicker' in window;
}

/**
 * Request directory handle for linked backups
 * @returns {Promise<FileSystemDirectoryHandle>} Directory handle
 */
export async function requestBackupDirectory() {
  if (!supportsFileSystemAccess()) {
    throw new Error('File System Access API not supported');
  }

  try {
    const directoryHandle = await window.showDirectoryPicker({
      mode: 'readwrite',
      startIn: 'documents'
    });
    
    // Store handle in IndexedDB for persistence
    const request = indexedDB.open('SeaReadyLinkedFolder', 1);
    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(['handles'], 'readwrite');
      const store = transaction.objectStore('handles');
      store.put(directoryHandle, 'backupDirectory');
    };
    
    return directoryHandle;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('User cancelled directory selection');
    }
    throw error;
  }
}

/**
 * Get stored directory handle
 * @returns {Promise<FileSystemDirectoryHandle|null>} Stored handle or null
 */
export async function getStoredBackupDirectory() {
  if (!supportsFileSystemAccess()) return null;

  return new Promise((resolve) => {
    const request = indexedDB.open('SeaReadyLinkedFolder', 1);
    
    request.onupgradeneeded = () => {
      const db = request.result;
      db.createObjectStore('handles');
    };
    
    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(['handles'], 'readonly');
      const store = transaction.objectStore('handles');
      const getRequest = store.get('backupDirectory');
      
      getRequest.onsuccess = () => {
        resolve(getRequest.result || null);
      };
      
      getRequest.onerror = () => {
        resolve(null);
      };
    };
    
    request.onerror = () => {
      resolve(null);
    };
  });
}